import React, { Fragment, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Flex, LoadingIndicator, NakedButton } from 'roo-ui/components';
import styled from '@emotion/styled';
import theme from 'lib/theme';
import FiltersButton from 'components/Filters/FiltersButton';
import PaymentPolicyFilter from 'pages/Property/Filters/PaymentPolicyFilter';
import { useDataLayer } from 'hooks/useDataLayer';
import { clearFilters, setFilters } from 'store/propertyAvailability/propertyAvailabilityActions';
import { getActiveFilters, getIsLoading, getTotalAvailableCount } from 'store/propertyAvailability/propertyAvailabilitySelectors';
import { setPropertyFiltersModalVisible } from 'store/ui/uiActions';
import Modal, { ModalFooter } from 'components/Modal';
import { useModal } from 'lib/hooks';

const LoaderOverlay = styled(Box)`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 2;
  pointer-events: none;
`;

const PhoneFiltersModal = ({ title, onClick }) => {
  const activeFilters = useSelector(getActiveFilters) || [];
  const resultCount = useSelector(getTotalAvailableCount);
  const isLoading = useSelector(getIsLoading);
  const { openModal, closeModal, modalProps } = useModal();
  const dispatch = useDispatch();

  const hasActiveFilters = !!activeFilters.length;

  const { emitInteractionEvent } = useDataLayer();

  const onCancel = useCallback(() => {
    closeModal();
    dispatch(setPropertyFiltersModalVisible(false));
    emitInteractionEvent({ type: 'Room Type Filters', value: 'Close Button Selected' });
  }, [closeModal, dispatch, emitInteractionEvent]);

  const onSubmit = useCallback(() => {
    closeModal();
    dispatch(setPropertyFiltersModalVisible(false));
    emitInteractionEvent({ type: 'Room Type Filters', value: 'View Results Button Selected' });
  }, [dispatch, emitInteractionEvent, closeModal]);

  const onChange = useCallback(
    (nextFilters) => {
      dispatch(setFilters({ ...nextFilters }));
    },
    [dispatch],
  );

  const onClearFilters = useCallback(() => {
    emitInteractionEvent({ type: 'Room Type Filters', value: 'Clear Filters Selected' });
    dispatch(clearFilters());
  }, [dispatch, emitInteractionEvent]);

  const onOpenModal = useCallback(() => {
    dispatch(setPropertyFiltersModalVisible(true));
    onClick();
    openModal();
    emitInteractionEvent({ type: 'Room Type Filters', value: 'Open Button Selected' });
  }, [openModal, dispatch, emitInteractionEvent, onClick]);

  const submitButtonText = `View ${resultCount} result${resultCount === 1 ? '' : 's'}`;

  return (
    <Fragment>
      <FiltersButton count={activeFilters.length} onClick={onOpenModal} />
      {modalProps.isOpen && (
        <Modal
          {...modalProps}
          onRequestClose={onCancel}
          title={title}
          padding={0}
          footerComponent={() => <ModalFooter submitModal={onSubmit} buttonText={submitButtonText} />}
        >
          <Box padding={4} bg="greys.porcelain" height="100%">
            {isLoading && (
              <LoaderOverlay>
                <LoadingIndicator />
              </LoaderOverlay>
            )}
            <Flex flexDirection="column">
              <Box borderBottom={`1px solid ${theme.colors.greys.alto}`} pb={4}>
                <NakedButton color="ui.link" disabled={!hasActiveFilters} onClick={onClearFilters} data-testid="clear-filters">
                  Clear filters
                </NakedButton>
              </Box>
              <Box py={4}>
                <PaymentPolicyFilter onChange={onChange} />
              </Box>
            </Flex>
          </Box>
        </Modal>
      )}
    </Fragment>
  );
};

PhoneFiltersModal.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

PhoneFiltersModal.defaultProps = {
  onClick: () => {},
};

export default PhoneFiltersModal;
